/**
 * Shared header config for authenticated pages.
 * Used by AuthenticatedHeader.
 */
import i18n from 'i18next';
import { legacyPathFor } from '@/config/movedPages';

export interface AuthenticatedHeaderConfig {
  title: string;
  subtitle: string;
  showUserInfo: boolean;
  showBalance: boolean;
  useRightSlot: boolean;
  /** When false, hide the back button (e.g. on Account Hub). */
  back?: boolean;
  backTo?: string;
}

export function getAuthenticatedHeaderConfig(pathname: string): AuthenticatedHeaderConfig {
  // The guided flows (exact paths): like the top-up and payout pages, balance on, avatar off.
  if (pathname === '/money/add' || pathname === '/money/cash-out') {
    return {
      title: i18n.t(pathname === '/money/add' ? 'money.add.title' : 'money.cashOut.title'),
      subtitle: '',
      showUserInfo: false,
      showBalance: true,
      useRightSlot: false,
      backTo: '/money',
    };
  }

  // Pages that moved under Money and Me keep their old page's config (matched below by old path).
  const path = legacyPathFor(pathname);

  if (path.includes('/account/finance/payment-checkout')) {
    return {
      title: i18n.t('pageTitles.paymentCheckout'),
      subtitle: i18n.t('pageSubtitles.completeDepositPayment'),
      showUserInfo: false,
      showBalance: false,
      useRightSlot: false,
      backTo: '/account/finance/deposit',
    };
  }

  if (path.includes('/account/finance/deposit')) {
    return {
      title: i18n.t('pageTitles.deposit'),
      subtitle: i18n.t('header.selectPlatformsToDeposit'),
      showUserInfo: true,
      showBalance: true,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/finance/withdrawals')) {
    return {
      title: i18n.t('pageTitles.withdraw'),
      subtitle: i18n.t('pageSubtitles.selectGamesAndAmountToWithdraw'),
      showUserInfo: true,
      showBalance: true,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/wallet/deposit-checkout')) {
    return {
      title: i18n.t('pageTitles.walletDepositCheckout'),
      subtitle: i18n.t('pageSubtitles.addFundsToWallet'),
      showUserInfo: false,
      showBalance: false,
      useRightSlot: false,
      backTo: '/account/finance/wallet/deposit',
    };
  }

  if (path.includes('/account/finance/wallet/deposit')) {
    return {
      title: i18n.t('pageTitles.walletTopUp'),
      subtitle: i18n.t('walletTopUp.subtitle'),
      showUserInfo: false,
      showBalance: true,
      useRightSlot: false,
      backTo: '/account/finance/wallet',
    };
  }

  if (path.includes('/account/wallet/payout-request')) {
    return {
      title: i18n.t('pageTitles.walletPayoutRequest'),
      subtitle: i18n.t('pageSubtitles.requestPayoutFromWallet'),
      showUserInfo: false,
      showBalance: true,
      useRightSlot: false,
      backTo: '/account/finance/wallet',
    };
  }

  if (path.includes('/account/finance/wallet')) {
    return {
      title: i18n.t('pageTitles.myWallet'),
      subtitle: i18n.t('pageSubtitles.manageWalletBalance'),
      showUserInfo: true,
      showBalance: true,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/settings')) {
    return {
      title: i18n.t('pageTitles.accountSettings'),
      subtitle: i18n.t('pageSubtitles.managePersonalInformation'),
      showUserInfo: true,
      showBalance: false,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/security') || path.includes('/account/change-password')) {
    return {
      title: i18n.t('pageTitles.accountSecurity'),
      subtitle: i18n.t('pageSubtitles.manageAccountSecurity'),
      showUserInfo: true,
      showBalance: false,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/games')) {
    return {
      title: i18n.t('pageTitles.gameManagement'),
      subtitle: i18n.t('pageSubtitles.manageGameAccountsAndBalances'),
      showUserInfo: true,
      showBalance: true,
      useRightSlot: false,
    };
  }

  if (path.includes('/account/transactions')) {
    return {
      title: i18n.t('pageTitles.transactionHistory'),
      subtitle: i18n.t('pageSubtitles.viewAllTransactions'),
      showUserInfo: true,
      showBalance: true,
      useRightSlot: false,
    };
  }

  return {
    title: i18n.t('header.accountHub'),
    subtitle: i18n.t('header.yourLuckyGamingPortal'),
    showUserInfo: true,
    showBalance: true,
    useRightSlot: false,
    back: false,
  };
}
